// src/pages/registeredUsers/StallRenewalPage.tsx
import React, { useState, useEffect, useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components'; // Import keyframes and css
import {useNavigate, useParams } from 'react-router-dom';
import {doc, getDoc } from 'firebase/firestore';
import {db } from '../../firebase';

// Import Font Awesome components and icons
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard, faMobileAlt, faMapMarkerAlt, faMoneyBillWave, faSpinner } from '@fortawesome/free-solid-svg-icons'; // Added faSpinner for loading
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';

// Re-using existing styles
import {Button, ErrorMessage } from './styles/StallRegistrationForm.styles';

// Import Timestamp type specifically from firebase/firestore
import {Timestamp } from 'firebase/firestore';

import {
  RenewalPageContainer,
  ContentBox,
  Title,
  Subtitle,
  BusinessImage,
  StallInfoGrid,
  InfoItem,
  StatusMessage,
  FormSection,
  PaymentOptionsContainer,
  PaymentHeader,
  PaymentButtonsGrid,
  PaymentButton,
  PaymentDisclaimer,
  SectionTitle,
  FormRow,
  FormGroup,
  Label,
  RequiredIndicator,
  RadioContainer,
  RadioLabel,
  RadioInput
} from './styles/renewalstyles';

// Define types for subscription options
type StallType = 'Premium' | 'Standard';
type SubscriptionDuration = 'Monthly' | '6-Monthly' | '12-Monthly';

// --- Styled Components ---

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const PaymentButtonLoading = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 15px;
  background-color: #e2e6ea;
  border: 1px solid #ced4da;
  border-radius: 10px;
  font-size: 1.1em;
  color: #495057;
  cursor: not-allowed;
  transition: all 0.3s ease;
  
  svg {
    font-size: 2.5em;
    margin-bottom: 10px;
    color: #007bff;
    animation: ${spin} 1s linear infinite;
  }
`;

const MessageContainer = styled.div`
  text-align: center;
  font-size: 1.1em;
  font-weight: bold;
  color: #007bff;
  margin-top: 20px;
`;

// Define the shape of your stall data from Firestore
interface StallData {
  stallNumber: string;
  businessName: string;
  businessPhone: string;
  email: string;
  contactPerson: string;
  googleMapLocation?: string;
  isActive: boolean;
  description?: string;
  whatsappPhone?: string;
  userId: string;
  location: {
    actual: string;
    district: string;
    parish: string;
    region: string;
    subcounty: string;
    village: string;
  };
  photoUrls?: {
    businessPhoto?: string;
    additionalPhotos?: string[];
  };
  subscription: {
    amount: number;
    durationMonths: number;
    status: string;
    // Add the new fields
    stallType: StallType;
    duration: SubscriptionDuration;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
  growth?: string;
  website?: string;
}

const StallRenewalPage: React.FC = () => {
  const navigate = useNavigate();
  const { stallNumber } = useParams<{ stallNumber: string }>();

  const [stallData, setStallData] = useState<StallData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitiatingPayment, setIsInitiatingPayment] = useState(false);
  const [pesapalMessage, setPesapalMessage] = useState('');

  // Add this constant for your Firebase function URL
  const FIREBASE_FUNCTION_URL = "https://us-central1-grema-62003.cloudfunctions.net/pesapal_payment";
  const callbackUrl = `${window.location.origin}/user-listings?stallNumber=${stallData?.stallNumber}`;

  const handlePayment = async (method: string) => {
    // Basic form validation
    if (!renewalOptions.stallType) {
      setFormErrors(prev => ({...prev, stallType: 'Please select a stall type.' }));
      return;
    }
    if (!renewalOptions.subscriptionDuration) {
      setFormErrors(prev => ({...prev, subscriptionDuration: 'Please select a subscription period.' }));
      return;
    }

    if (method !== 'Pesapal') {
      alert(`Payment method ${method} is not yet implemented. Only Pesapal is available.`);
      return;
    }

    setIsInitiatingPayment(true);
    setPesapalMessage('Initializing payment...');
    console.log("Starting Pesapal payment process...");

    try {
      // Construct the payment payload
      const paymentPayload = {
        id: `RENEW-${Date.now()}-${stallData?.stallNumber}`, // Unique transaction ID
        currency: "UGX",
        amount: calculatedAmount,
        description: `Stall Renewal for ${stallData?.businessName} - ${renewalOptions.stallType} (${renewalOptions.subscriptionDuration})`,
        callback_url: callbackUrl,
        notification_id: "c2f63263-791f-4742-87de-db6d5629e7ea", // From your Pesapal dashboard
        branch: "Main Branch",
        billing_address: {
          email_address: stallData?.email || '',
          phone_number: stallData?.businessPhone || '',
          country_code: "UG",
          first_name: stallData?.businessName || '',
          middle_name: "",
          last_name: "",
          line_1: `${stallData?.location.village}, ${stallData?.location.parish}`,
          line_2: `${stallData?.location.subcounty}, ${stallData?.location.district}`,
          city: stallData?.location.district || '',
          state: stallData?.location.region || '',
          postal_code: "",
          zip_code: "",
        },
      };

      console.log("Pesapal Payload:", paymentPayload);

      // Call Firebase function via fetch
      const response = await fetch(FIREBASE_FUNCTION_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(paymentPayload),
      });

      console.log("Firebase Function Response Status:", response.status);

      if (response.ok) {
        const result = await response.json();
        console.log("Pesapal Response:", result);

        if (result.payment_url) {
          console.log("Redirecting to Pesapal:", result.payment_url);
          setPesapalMessage('Redirecting to payment gateway...');
          // Store renewal data in sessionStorage to retrieve after payment
          sessionStorage.setItem('pendingRenewal', JSON.stringify({
            stallNumber: stallData?.stallNumber,
            stallType: renewalOptions.stallType,
            subscriptionDuration: renewalOptions.subscriptionDuration,
            amount: calculatedAmount
          }));
          window.location.href = result.payment_url;
        } else {
          console.error("No payment URL returned from Firebase function.");
          setPesapalMessage('Payment initialization failed: No redirect URL received.');
          alert("Payment initialization failed: No redirect URL received.");
        }
      } else {
        const errorText = await response.text();
        console.error("Function call failed:", errorText);
        setPesapalMessage(`Payment initialization failed: ${errorText}`);
        throw new Error(errorText);
      }
    } catch (error: any) {
      console.error("Payment initialization error:", error);
      setPesapalMessage(`Payment initialization failed: ${error.message}`);
      alert(`Payment initialization failed: ${error.message}`);
    } finally {
      setIsInitiatingPayment(false);
    }
  };

  // New state for subscription form
  const [renewalOptions, setRenewalOptions] = useState<{
    stallType: StallType | null;
    subscriptionDuration: SubscriptionDuration | null;
  }>({
    stallType: null,
    subscriptionDuration: null,
  });
  const [formErrors, setFormErrors] = useState({
    stallType: '',
    subscriptionDuration: '',
  });

  // Define prices and discounts
  const prices = useMemo(() => ({
    // Standard: {
    //   Monthly: 100000,
    //   '6-Monthly': 100000 * 6 * 0.8, // 20% discount
    //   '12-Monthly': 100000 * 12 * 0.7, // 30% discountfire
    // },
     Standard: {
      Monthly: 500,
      '6-Monthly': 500 * 6 * 0.8, // 20% discount
      '12-Monthly': 5000 * 12 * 0.7, // 30% discount
    },
    Premium: {
      Monthly: 150000,
      '6-Monthly': 150000 * 6 * 0.8,
      '12-Monthly': 150000 * 12 * 0.7,
    },
  }), []);

  // Calculate the total amount based on selections
  const calculatedAmount = useMemo(() => {
    if (renewalOptions.stallType && renewalOptions.subscriptionDuration) {
      return prices[renewalOptions.stallType][renewalOptions.subscriptionDuration];
    }
    return null;
  }, [renewalOptions, prices]);

  useEffect(() => {
    const fetchStallData = async (stallNum: string) => {
      setLoading(true);
      setError(null);
      try {
        const stallDocRef = doc(db, 'stalls', stallNum);
        const docSnap = await getDoc(stallDocRef);

        if (docSnap.exists()) {
          const data = docSnap.data() as StallData;

          const processedData: StallData = {
            ...data,
            location: data.location || {actual: '', district: '', parish: '', region: '', subcounty: '', village: '' },
            subscription: data.subscription || {amount: 0, durationMonths: 0, status: 'unknown' },
            photoUrls: data.photoUrls || {},
            whatsappPhone: data.whatsappPhone || undefined,
          };
          setStallData(processedData);
        } else {
          setError(`Stall with number "${stallNum}" not found.`);
          setStallData(null);
        }
      } catch (err: any) {
        console.error("Error fetching stall data:", err);
        setError(`Failed to load stall details: ${err.message}`);
        setStallData(null);
      } finally {
        setLoading(false);
      }
    };

    if (stallNumber) {
      fetchStallData(stallNumber);
    } else {
      setError("No stall number provided in the URL.");
    }
  }, [stallNumber]);


  if (loading) {
    return (
      <RenewalPageContainer>
        <ContentBox>
          <p>Loading stall renewal details...</p>
        </ContentBox>
      </RenewalPageContainer>
    );
  }

  if (error || !stallData) {
    return (
      <RenewalPageContainer>
        <ContentBox>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {!error && <ErrorMessage>No stall data available. Please log in again to access.</ErrorMessage>}
          <Button onClick={() => navigate('/stall-renewal-login')} style={{marginTop: '10px' }}>
            Go to Login
          </Button>
          <Button onClick={() => navigate('/')} style={{marginTop: '10px' }}>
            Go to Home
          </Button>
        </ContentBox>
      </RenewalPageContainer>
    );
  }

  return (
    <RenewalPageContainer>
      <ContentBox>
        <Title>Stall Renewal</Title>
        <Subtitle>Details for {stallData.businessName || 'Your Stall'}</Subtitle>

        {stallData.photoUrls?.businessPhoto && (
          <BusinessImage src={stallData.photoUrls.businessPhoto} alt={`${stallData.businessName} Business Photo`} />
        )}

        <StallInfoGrid>
          <InfoItem>
            <strong>Stall Number:</strong>
            <span>{stallData.stallNumber}</span>
          </InfoItem>
          <InfoItem>
            <strong>Business Name:</strong>
            <span>{stallData.businessName}</span>
          </InfoItem>
          <InfoItem>
            <strong>Contact Person:</strong>
            <span>{stallData.contactPerson}</span>
          </InfoItem>
          <InfoItem>
            <strong>Contact Phone:</strong>
            <span>{stallData.businessPhone}</span>
          </InfoItem>
          {stallData.whatsappPhone && (
             <InfoItem>
               <strong>WhatsApp:</strong>
               <span>
                 <FontAwesomeIcon icon={faWhatsapp} style={{marginRight: '5px' }} />
                 {stallData.whatsappPhone}
               </span>
             </InfoItem>
           )}
          <InfoItem>
            <strong>Contact Email:</strong>
            <span>{stallData.email}</span>
          </InfoItem>
          <InfoItem>
            <strong>Stall Location:</strong>
            <span>
              {stallData.location.actual}, {stallData.location.village}, {stallData.location.parish}, {stallData.location.subcounty}, {stallData.location.district}, {stallData.location.region}
            </span>
          </InfoItem>
          {stallData.googleMapLocation && (
            <InfoItem>
              <strong>Google Map Ref:</strong>
              <span>
                 <FontAwesomeIcon icon={faMapMarkerAlt} style={{marginRight: '5px' }} />
                 {stallData.googleMapLocation}
              </span>
            </InfoItem>
          )}
          {stallData.description && (
            <InfoItem style={{gridColumn: '1 / -1' }}>
              <strong>Description:</strong>
              <span>{stallData.description}</span>
            </InfoItem>
          )}
        </StallInfoGrid>

        <StatusMessage isActive={stallData.isActive}>
          Current Stall Status: {stallData.isActive ? 'Active' : 'Inactive (Payment Required)'}
        </StatusMessage>

        {stallData.subscription.status !== 'active' && (
          <>
            <FormSection>
              <SectionTitle>
                <FontAwesomeIcon icon={faMoneyBillWave} />
                <h3>Stall Type & Subscription</h3>
              </SectionTitle>

              <FormRow>
                <FormGroup error={!!formErrors.stallType}>
                  <Label>Stall Type <RequiredIndicator>*</RequiredIndicator></Label>
                  <RadioContainer>
                    <RadioLabel>
                      <RadioInput
                        type="radio"
                        name="stallType"
                        value="Premium"
                        checked={renewalOptions.stallType === 'Premium'}
                        onChange={(e: { target: { value: string; }; }) => {
                          setRenewalOptions(prev => ({...prev, stallType: e.target.value as StallType }));
                          setFormErrors(prev => ({...prev, stallType: '' }));
                        }}
                      />
                      Premium
                    </RadioLabel>
                    <RadioLabel>
                      <RadioInput
                        type="radio"
                        name="stallType"
                        value="Standard"
                        checked={renewalOptions.stallType === 'Standard'}
                        onChange={(e: { target: { value: string; }; }) => {
                          setRenewalOptions(prev => ({...prev, stallType: e.target.value as StallType }));
                          setFormErrors(prev => ({...prev, stallType: '' }));
                        }}
                      />
                      Standard
                    </RadioLabel>
                  </RadioContainer>
                  {formErrors.stallType && <ErrorMessage>{formErrors.stallType}</ErrorMessage>}
                </FormGroup>
              </FormRow>

              {renewalOptions.stallType && (
                <FormRow>
                  <FormGroup error={!!formErrors.subscriptionDuration}>
                    <Label>Subscription Period <RequiredIndicator>*</RequiredIndicator></Label>
                    <RadioContainer>
                      <RadioLabel>
                        <RadioInput
                          type="radio"
                          name="subscriptionDuration"
                          value="Monthly"
                          checked={renewalOptions.subscriptionDuration === 'Monthly'}
                          onChange={(e: { target: { value: string; }; }) => {
                            setRenewalOptions(prev => ({...prev, subscriptionDuration: e.target.value as SubscriptionDuration }));
                            setFormErrors(prev => ({...prev, subscriptionDuration: '' }));
                          }}
                        />
                        Monthly
                      </RadioLabel>
                      <RadioLabel>
                        <RadioInput
                          type="radio"
                          name="subscriptionDuration"
                          value="6-Monthly"
                          checked={renewalOptions.subscriptionDuration === '6-Monthly'}
                          onChange={(e: { target: { value: string; }; }) => {
                            setRenewalOptions(prev => ({...prev, subscriptionDuration: e.target.value as SubscriptionDuration }));
                            setFormErrors(prev => ({...prev, subscriptionDuration: '' }));
                          }}
                        />
                        6-Monthly (20% off)
                      </RadioLabel>
                      <RadioLabel>
                        <RadioInput
                          type="radio"
                          name="subscriptionDuration"
                          value="12-Monthly"
                          checked={renewalOptions.subscriptionDuration === '12-Monthly'}
                          onChange={(e: { target: { value: string; }; }) => {
                            setRenewalOptions(prev => ({...prev, subscriptionDuration: e.target.value as SubscriptionDuration }));
                            setFormErrors(prev => ({...prev, subscriptionDuration: '' }));
                          }}
                        />
                        12-Monthly (30% off)
                      </RadioLabel>
                    </RadioContainer>
                    {formErrors.subscriptionDuration && <ErrorMessage>{formErrors.subscriptionDuration}</ErrorMessage>}
                  </FormGroup>
                </FormRow>
              )}

              {calculatedAmount !== null && (
                <p style={{ fontWeight: 'bold', fontSize: '1.2em', marginTop: '10px' }}>
                  Total Amount: UGX {calculatedAmount.toLocaleString()}
                </p>
              )}
            </FormSection>

            <PaymentOptionsContainer>
              <PaymentHeader>Select Your Renewal Payment Method</PaymentHeader>
              <PaymentButtonsGrid>
                <PaymentButton 
                  onClick={() => handlePayment('Pesapal')}
                  disabled={isInitiatingPayment}
                >
                  {isInitiatingPayment ? (
                    <PaymentButtonLoading>
                      <FontAwesomeIcon icon={faSpinner} />
                      <p>{pesapalMessage}</p>
                    </PaymentButtonLoading>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faCreditCard} />
                      Pesapal
                    </>
                  )}
                </PaymentButton>
              </PaymentButtonsGrid>
              {error && <ErrorMessage style={{marginTop: '20px' }}>{error}</ErrorMessage>}
            </PaymentOptionsContainer>
          </>
        )}

        {stallData.subscription.status === 'active' && (
          <PaymentDisclaimer style={{backgroundColor: '#d4edda', borderColor: '#28a745' }}>
            Your stall subscription is currently **Active**. No immediate renewal is required.
            You can renew closer to your subscription end date.
          </PaymentDisclaimer>
        )}
      </ContentBox>
    </RenewalPageContainer>
  );
};

export default StallRenewalPage;